/**
 * "My work" — for the signed-in person, the queues where they can act now, and how much waits in
 * each (bata-ui#166 W0-8, DEC-01, bata-ui ADR 9022). Pure: the screen feeds it the reads and the
 * capability verdicts, and `my-work.test.ts` pins every rule on plain values.
 *
 * **Counts come only from reads that exist today, and never from a guess.** Each queue names the
 * read its count is from:
 *
 * | Queue | Capability | Count |
 * |---|---|---|
 * | replenishment proposals | `replenishment_proposal · dispose` | `proposals_served_pending`, decision summary of the newest in-season run; opens on the pending lines. Absent (not published) on a platform that does not state it: `proposals_pending` counts the dropped-door markers, which the pending lines do not list |
 * | transfers | `move_line · dispose` | `transfers_pending` of the same summary — the one run's undecided lines (v0.126.0), opened on `transfers_pending_move_run_id` |
 * | price steps | `price_action · dispose` | `price_actions_pending` of the price run's summary (the Cockpit's F16 run) |
 * | pending approvals | `parameter_change · approve` | change sets where the register says `callerMayApprove` |
 * | execution pack, to export | `execution_pack · execute` | pack lines still `approved`, newest run |
 * | execution pack, to confirm | `execution_pack · confirm` | pack lines `dispatched`, newest run |
 * | my alerts | `alert · read` | `alerts.assignedGroups` of `GET /me/work-counts` when sent (DEC-101), else `alerts.assignedOpen` (W3-4) |
 * | alerts to take | `alert · assign` | `alerts.claimableGroups` of the same read when sent, else `alerts.claimable` |
 *
 * A read that has not answered is `loading`; one that failed or does not exist on this platform is
 * an absence with its reason — never a zero.
 *
 * **Extension point.** A new queue is one more {@link WorkQueueId}, one entry in {@link QUEUE_SPEC}
 * and one {@link QueueCount} fed from its read; nothing else here changes. The two alert queues
 * arrived that way (bata-ui#166 W3-4, `docs/adr/9101`): `/me/work-counts` counts what is assigned
 * to this person and what their role may take, with the queue read's own predicates, so the
 * count and the queue it opens cannot disagree.
 */

import type { ChangeSetSummary, DecisionSummary, WorkCounts } from '@/api/generated';
import type { CapabilityAction, CapabilityKind, CapabilityVerdict } from '@/auth/capabilities';

import { PENDING_PROPOSALS_PARAMS, PENDING_TRANSFERS_PATH } from '@/features/inseason/pending-links';

import { RAIL_APPS, type RailAppDefinition, type RailAppEntry } from '../../chrome/rail-layout';
import { alertGroupCounts } from './work-counts';

export const MY_WORK_PATH = '/my-work';

export type WorkQueueId =
  | 'replenishment' | 'transfers' | 'priceSteps' | 'approvals' | 'packExport' | 'packConfirm'
  | 'alertsMine' | 'alertsToTake';

/** How much waits in a queue, as far as a read says. */
export type QueueCount =
  | { readonly kind: 'loading' }
  /** `groups`: the value counts alert groups (DEC-101), not alerts. */
  | { readonly kind: 'count'; readonly value: number; readonly groups?: true }
  | { readonly kind: 'absent'; readonly reasonKey: string };

export interface QueueSpec {
  readonly labelKey: string;
  readonly capability: { readonly kind: CapabilityKind; readonly action: CapabilityAction };
  /** Where the person acts on it. The run travels as `?runId=` where the queue is a run's. */
  readonly path: string;
  readonly runBound: boolean;
  /** Query parameters the queue opens with — the alert queue's `?queue=&state=`. */
  readonly params?: Readonly<Record<string, string>>;
}

/** Spelled out for the catalogue check; the order is the order on screen. */
export const QUEUE_SPEC: Readonly<Record<WorkQueueId, QueueSpec>> = {
  replenishment: {
    labelKey: 'myWork.queue.replenishment',
    capability: { kind: 'replenishment_proposal', action: 'dispose' },
    // The decision panel's page since client rule 3 split the allocation screen (ADR 6006), on the
    // record's pending lines: the count is `proposals_served_pending`, and that filter is exactly it.
    path: '/inseason/allocation-decisions',
    runBound: true,
    params: PENDING_PROPOSALS_PARAMS,
  },
  transfers: {
    labelKey: 'myWork.queue.transfers',
    // The one approval process for every store-to-store move (bata-reqs ADR 9002 stage 3): the one run's merged
    // list, opened on the move run the count is of (`transfers_pending_move_run_id`), never on the cycle run.
    capability: { kind: 'move_line', action: 'dispose' },
    path: PENDING_TRANSFERS_PATH,
    runBound: false,
  },
  priceSteps: {
    labelKey: 'myWork.queue.priceSteps',
    capability: { kind: 'price_action', action: 'dispose' },
    // Pricing ▸ Pending approvals, where the price queue is decided (package A, client rule 3).
    path: '/inseason/markdown/approvals',
    runBound: true,
  },
  approvals: {
    labelKey: 'myWork.queue.approvals',
    capability: { kind: 'parameter_change', action: 'approve' },
    // The pending-approvals queue, narrowed to the sets this person may approve: the register's page
    // is a different list (every parameter), and this count is the sets the register says
    // `callerMayApprove`. The key is the queue's own (`MAY_APPROVE_PARAM` in the foundations
    // chapter's `parameters-v7/approvals.ts`, which a test holds this literal to): My Work only
    // writes it into the link, it reads nothing of it.
    path: '/foundations/parameters/approvals',
    runBound: false,
    params: { 'ap.may': 'approve' },
  },
  packExport: {
    labelKey: 'myWork.queue.packExport',
    capability: { kind: 'execution_pack', action: 'execute' },
    path: '/inseason/write-back/pack',
    runBound: true,
  },
  packConfirm: {
    labelKey: 'myWork.queue.packConfirm',
    capability: { kind: 'execution_pack', action: 'confirm' },
    path: '/inseason/write-back/pack',
    runBound: true,
  },
  alertsMine: {
    labelKey: 'myWork.queue.alertsMine',
    capability: { kind: 'alert', action: 'read' },
    path: '/foundations/exceptions',
    runBound: false,
    params: { queue: 'mine', state: 'open' },
  },
  alertsToTake: {
    labelKey: 'myWork.queue.alertsToTake',
    capability: { kind: 'alert', action: 'assign' },
    path: '/foundations/exceptions',
    runBound: false,
    params: { queue: 'claimable', state: 'open' },
  },
};

export const WORK_QUEUE_IDS = Object.keys(QUEUE_SPEC) as readonly WorkQueueId[];

export const MY_WORK_REASON = {
  noRun: 'myWork.reason.noRun',
  readFailed: 'myWork.reason.readFailed',
  notPublished: 'myWork.reason.notPublished',
  transfersNotRecorded: 'myWork.reason.transfersNotRecorded',
  approvalNotPublished: 'myWork.reason.approvalNotPublished',
  alertsNotGranted: 'myWork.reason.alertsNotGranted',
} as const;

/** A read's outcome, reduced to what a count needs. */
export type Read<T> =
  | { readonly kind: 'loading' }
  | { readonly kind: 'ready'; readonly data: T }
  | { readonly kind: 'failed'; readonly notPublished: boolean };

function fromRead<T>(read: Read<T> | null, count: (data: T) => QueueCount): QueueCount {
  if (read === null) return { kind: 'absent', reasonKey: MY_WORK_REASON.noRun };
  if (read.kind === 'loading') return { kind: 'loading' };
  if (read.kind === 'failed') {
    return { kind: 'absent', reasonKey: read.notPublished ? MY_WORK_REASON.notPublished : MY_WORK_REASON.readFailed };
  }
  return count(read.data);
}

export interface WorkSources {
  /** The newest in-season run's decision summary; `null` when there is no run. */
  readonly summary: Read<DecisionSummary> | null;
  /** The price run's summary (the same entry when it is the same run); `null` with no run. */
  readonly priceSummary: Read<DecisionSummary> | null;
  /** `GET /registry/changes?state=proposed`. */
  readonly pendingChanges: Read<readonly ChangeSetSummary[]>;
  /** The newest run's execution pack, as tallied: lines to export and to confirm. */
  readonly pack: Read<{ readonly toExport: number; readonly awaitingConfirmation: number }> | null;
  /** `GET /me/work-counts` (W3-4). */
  readonly workCounts: Read<WorkCounts>;
}

/** An alert count, or the reason the platform gives for not counting alerts for this person. */
function alertCount(
  counts: WorkCounts, pick: (alerts: NonNullable<WorkCounts['alerts']>) => number, groups: number | null = null,
): QueueCount {
  const alerts = counts.alerts ?? null;
  if (alerts === null) {
    return {
      kind: 'absent',
      reasonKey: counts.alertsUnavailableReason === 'not_granted' ? MY_WORK_REASON.alertsNotGranted : MY_WORK_REASON.notPublished,
    };
  }
  return groups === null ? { kind: 'count', value: pick(alerts) } : { kind: 'count', value: groups, groups: true };
}

/** The alerts assigned to this person that are past due — shown beside "my alerts", or `null`. */
export function alertsOverdue(read: Read<WorkCounts>): number | null {
  if (read.kind !== 'ready') return null;
  const alerts = read.data.alerts ?? null;
  return alerts === null ? null : alerts.assignedOverdue;
}

/** The count of each queue, from the reads. */
export function queueCounts(sources: WorkSources): Readonly<Record<WorkQueueId, QueueCount>> {
  return {
    // The served count only: the older `proposals_pending` includes the dropped-door markers, which the pending lines this queue
    // opens do not list, so a platform that does not state the served count has no number the list could total (ADR 9069).
    replenishment: fromRead(sources.summary, (summary) => (summary.proposals_served_pending === undefined
      ? { kind: 'absent', reasonKey: MY_WORK_REASON.notPublished }
      : { kind: 'count', value: summary.proposals_served_pending })),
    transfers: fromRead(sources.summary, (summary) => (summary.transfers_pending === null || !summary.transfer_dispositions_recorded
      ? { kind: 'absent', reasonKey: MY_WORK_REASON.transfersNotRecorded }
      : { kind: 'count', value: summary.transfers_pending })),
    priceSteps: fromRead(sources.priceSummary, (summary) => ({ kind: 'count', value: summary.price_actions_pending })),
    approvals: fromRead(sources.pendingChanges, (changes) => {
      // Every row must carry the register's answer: counting "all pending" for a platform that
      // does not say who may approve would count sets this person cannot approve.
      if (changes.some((change) => change.approval === null || change.approval === undefined)) {
        return { kind: 'absent', reasonKey: MY_WORK_REASON.approvalNotPublished };
      }
      return { kind: 'count', value: changes.filter((change) => change.approval?.callerMayApprove === true).length };
    }),
    packExport: fromRead(sources.pack, (pack) => ({ kind: 'count', value: pack.toExport })),
    packConfirm: fromRead(sources.pack, (pack) => ({ kind: 'count', value: pack.awaitingConfirmation })),
    // DEC-101 (ADR 9040): groups, not alerts, when the platform counts them.
    alertsMine: fromRead(sources.workCounts, (counts) => alertCount(counts, (alerts) => alerts.assignedOpen, alertGroupCounts(counts).assigned)),
    alertsToTake: fromRead(sources.workCounts, (counts) => alertCount(counts, (alerts) => alerts.claimable, alertGroupCounts(counts).claimable)),
  };
}

/** The caller's own pending sets that wait on another approver (DEC-03) — shown, not counted as work. */
export function awaitingOthers(changes: readonly ChangeSetSummary[]): number {
  return changes.filter((change) => change.approval?.callerBlockedBy === 'author').length;
}

export interface WorkQueue {
  readonly id: WorkQueueId;
  readonly spec: QueueSpec;
  readonly verdict: CapabilityVerdict;
  readonly count: QueueCount;
}

export interface WorkPlan {
  /** Queues this person may act on (or whose permission is not known yet), most waiting first. */
  readonly mine: readonly WorkQueue[];
  /** Queues their roles do not grant, each with the platform's reason. */
  readonly notMine: readonly WorkQueue[];
}

const waiting = (queue: WorkQueue): number => (queue.count.kind === 'count' ? queue.count.value : -1);

export function workPlan(
  verdicts: Readonly<Record<WorkQueueId, CapabilityVerdict>>,
  counts: Readonly<Record<WorkQueueId, QueueCount>>,
): WorkPlan {
  const queues = WORK_QUEUE_IDS.map((id): WorkQueue => ({ id, spec: QUEUE_SPEC[id], verdict: verdicts[id], count: counts[id] }));
  const order = new Map(WORK_QUEUE_IDS.map((id, index) => [id, index]));
  return {
    // Something waiting first, then the declared order: a stable page, not one that reshuffles.
    mine: queues
      .filter((queue) => queue.verdict.state !== 'denied')
      .sort((a, b) => Number(waiting(b) > 0) - Number(waiting(a) > 0) || (order.get(a.id) ?? 0) - (order.get(b.id) ?? 0)),
    notMine: queues.filter((queue) => queue.verdict.state === 'denied'),
  };
}

/**
 * The rail app a queue's page sits under — «App» on the page (brief part 1 §5 screen 06). The rail's
 * own layout decides it, not a list kept here: the longest claiming entry the path is at or under.
 *
 * A plain entry claims its page. An entry that opens with a query is usually another app's view of
 * a shared page (Pricing's «Parametri» is the register opened on `?app=pricing`; Simulate on
 * `?stage=`; Documentation on `?section=`), so it does not claim it. The one exception is the
 * shared page's GROUP OWNER: the entry that opens the page on its own app AND draws the page's
 * sub-entries — Management's «Parametri generali» (`?app=management`, Registro · Scadenze e
 * storico). So the parameter register and its queue are Management's, and the per-app `?app=`
 * entries still claim nothing. `null`: the rail does not place it.
 */
export function queueApp(path: string, apps: readonly RailAppDefinition[] = RAIL_APPS): RailAppDefinition | null {
  let best: { app: RailAppDefinition; length: number } | null = null;
  for (const app of apps) {
    for (const entry of app.entries) {
      if (entry.query !== undefined && !ownsSharedPage(app, entry)) continue;
      if (path !== entry.to && !path.startsWith(`${entry.to}/`)) continue;
      if (best === null || entry.to.length > best.length) best = { app, length: entry.to.length };
    }
  }
  return best?.app ?? null;
}

/** A query entry that is its page's group owner: opened on its own app, drawing the page's sub-entries. */
function ownsSharedPage(app: RailAppDefinition, entry: RailAppEntry): boolean {
  return entry.subEntries === 'manifest'
    && entry.query !== undefined
    && Object.values(entry.query).includes(app.id);
}
